import { motion } from "framer-motion";
import { Car, Tent, Sun } from "lucide-react";

const PHASES = [
  {
    num: "01",
    status: "Live Now",
    icon: Car,
    accent: "var(--shade)",
    title: "Vehicle Shade Parking",
    body: "Precision shade for every parking bay on earth. We map structures, trees and overhangs to tell you exactly where your cabin stays cool — before you arrive.",
    meta: "144,000 bays indexed",
    img: "https://images.unsplash.com/photo-1781409884255-bf4a3826a7dd?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Car resting in structured shade",
  },
  {
    num: "02",
    status: "Expanding",
    icon: Tent,
    accent: "var(--shade)",
    title: "Camping & Picnic Canopy",
    body: "The Growth Path moves beyond the lot. Tree-line canopy modelling finds the coolest pitch for your tent and the perfect shaded lawn for a long picnic.",
    meta: "Tree-line beta in 12 regions",
    img: "https://images.unsplash.com/photo-1507777767380-68bdac55c642?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Forest canopy over a campsite",
  },
  {
    num: "03",
    status: "Planned",
    icon: Sun,
    accent: "var(--sun)",
    title: "Sunbathing Golden Spots",
    body: "The inverse problem, solved. When you want the sun, we find the spot with maximum golden exposure — down to the lounger, timed to the minute.",
    meta: "Exposure engine in design",
    img: "https://images.unsplash.com/photo-1760445645512-e5dbf41503e4?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Golden sunlight across open ground",
  },
];

export default function GrowthPath() {
  return (
    <section
      id="growth-path"
      data-testid="growth-path-section"
      className="border-b border-[var(--sp-line)] bg-[var(--sp-surface)]"
    >
      <div className="mx-auto max-w-[1500px] px-5 py-20 sm:px-8 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="mb-4 flex items-center gap-3 font-mono2 text-xs uppercase tracking-[0.28em] text-[var(--sun)]"
            >
              <span className="h-px w-10 bg-[var(--sun)]" />
              The Growth Path // 02
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-2xl font-display text-2xl font-extrabold uppercase leading-none tracking-tight sm:text-4xl lg:text-5xl"
            >
              From parking bays to perfect sun spots
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-sm text-sm leading-relaxed text-[var(--sp-muted)]"
          >
            One engine, three frontiers of comfort. The same solar intelligence that shades your
            car will soon shade your campsite — and find your place in the sun.
          </motion.p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-px border border-[var(--sp-line)] bg-[var(--sp-line)] lg:grid-cols-3">
          {PHASES.map((p, i) => (
            <motion.article
              key={p.num}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`growth-path-card-${p.num}`}
              className="group bg-[var(--sp-bg)]"
            >
              <div className="relative overflow-hidden">
                <img
                  src={p.img}
                  alt={p.alt}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover grayscale contrast-125 transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                />
                <div className="absolute inset-0 bg-[var(--sun)] mix-blend-multiply opacity-20 transition-opacity duration-700 group-hover:opacity-0" />
                <span
                  data-testid={`growth-path-status-${p.num}`}
                  className={`absolute left-0 top-4 px-3 py-1.5 font-mono2 text-[10px] font-bold uppercase tracking-[0.14em] ${
                    p.status === "Live Now" ? "bg-[var(--sun)] text-black" : "bg-black text-[var(--sun)]"
                  }`}
                >
                  {p.status}
                </span>
              </div>
              <div className="p-8">
                <div className="flex items-center justify-between">
                  <span className="font-mono2 text-xs uppercase tracking-[0.28em] text-[var(--sp-faint)]">
                    Phase {p.num}
                  </span>
                  <p.icon className="h-5 w-5" style={{ color: p.accent }} />
                </div>
                <h3 className="mt-4 font-display text-xl font-bold uppercase tracking-tight sm:text-2xl">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[var(--sp-muted)]">{p.body}</p>
                <p className="mt-6 border-t border-[var(--sp-line)] pt-4 font-mono2 text-[10px] uppercase tracking-[0.14em] text-[var(--sp-faint)]">
                  {p.meta}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
